import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { z } from 'zod'
import useQueryGetMinimumWithdraw from '@/modules/manage-player/api/hooks/useQueryGetMinimumWithdraw'
import useMutationUpdateMinimumWithdraw from '@/modules/manage-player/api/hooks/useMutationUpdateMinimumWithdraw'
import Spinner from '@/common/components/Spinner'
import useHasPermission from '@/common/hooks/useHasPermission'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { WalletType } from '@/schema'

export const walletTypeLabels: Record<string, string> = {
	[WalletType.MAIN]: 'เงิน',
	[WalletType.PROMOTION]: 'เงินติดเงื่อนไข promotion',
	[WalletType.INVITE_FRIEND]: 'กิจกรรมชวนเพื่อน',
	[WalletType.RETURN_LOSS]: 'กิจกรรมคืนยอดเสีย',
	[WalletType.COMMISSION]: 'กิจกรรมคอมมิชชั่น',
	[WalletType.WHEEL]: 'กิจกรรมหมุนวงล้อ',
	[WalletType.DAILY_CHECK_IN]: 'กิจกรรมเช็คอินรายวัน',
	[WalletType.PARTNER]: 'พาร์ทเนอร์',
}

const formSchema = z.object({
	minimumAmount: z.number().min(0, 'จำนวนต้องไม่ต่ำกว่า 0'),
})

type FormValues = z.infer<typeof formSchema>

interface MinimumWithdrawBoxProps {
	walletType: WalletType
}

interface MinimumWithdrawSetting {
	walletType: WalletType
	minimumAmount: number
}

export default function MinimumWithdrawBox({
	walletType,
}: MinimumWithdrawBoxProps) {
	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)

	const { data, isLoading } = useQueryGetMinimumWithdraw() as unknown as {
		data: MinimumWithdrawSetting[]
		isLoading: boolean
	}
	const { mutate: updateMinimumWithdraw } = useMutationUpdateMinimumWithdraw()

	const findSettingByWalletType = (type: string) => {
		return data?.find(
			(setting: MinimumWithdrawSetting) => setting.walletType === type,
		)
	}

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		values: {
			minimumAmount: findSettingByWalletType(walletType)?.minimumAmount ?? 0,
		},
	})

	if (isLoading) {
		return <Spinner />
	}

	const handleSubmit = (values: FormValues) => {
		try {
			updateMinimumWithdraw(
				{
					payload: {
						walletType,
						minimumAmount: values.minimumAmount,
					},
				},
				{
					onSuccess: () => {
						toast.success('บันทึกการตั้งค่าเรียบร้อยแล้ว')
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)}>
				<div
					className="grid grid-cols-3 items-end gap-4"
					style={{ gridTemplateColumns: '2fr 2fr 1fr' }}
				>
					<div className="whitespace-nowrap font-medium">
						{walletTypeLabels[walletType]}
					</div>
					<FormField
						control={form.control}
						name="minimumAmount"
						render={({ field }) => (
							<FormItem className="mb-0">
								<FormLabel className="sr-only">จำนวนถอนขั้นต่ำ</FormLabel>
								<FormControl>
									<Input
										type="number"
										placeholder="กรอกจำนวนถอนขั้นต่ำ"
										{...field}
										onChange={(e) =>
											field.onChange(
												e.target.value ? Number(e.target.value) : null,
											)
										}
										disabled={!hasWritePermission}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<Button type="submit" disabled={!hasWritePermission}>
						บันทึก
					</Button>
				</div>
			</form>
		</Form>
	)
}
